// Report the open conversation to the Sculpt team: a reason, an optional comment, and a person reads it — the API
// mails staff, who resolve it in the backoffice. Where a rating is counted, a report is answered by someone.
//
// Rendered in flow above the composer, like the feedback card and for the same reason: in a ~320px pane an overlay
// would cover the textarea the user is about to reach for.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isTooManyReports } from '../../services/ApiService';
import { REPORT_REASONS, type ReportReason } from '../../types/agent';

const CHOSEN = 'border-accent bg-accent-bg text-accent-dark';
const IDLE = 'border-line-btn text-muted hover:bg-hover hover:text-ink';

/** How long the confirmation stays before the panel closes itself. */
export const SENT_FOR_MS = 2400;

/** Flag icon (Lucide `flag`, the 24×24 stroke set of Header): the chat header's "report this conversation". */
export function FlagIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <path d="M4 22v-7" />
    </svg>
  );
}

type Phase = 'edit' | 'sending' | 'sent' | 'failed' | 'tooMany';

/** The report form. Send waits for a reason; the comment is optional. A failed send keeps what was typed so the user
 *  can try again; past the daily cap Send stays off. */
export function ReportPanel({ onSubmit, onClose }: {
  onSubmit: (reason: ReportReason, comment: string) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [comment, setComment] = useState('');
  const [phase, setPhase] = useState<Phase>('edit');
  // The pane re-renders on every streamed token: the timer must not restart each time `onClose` is a new function.
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    if (phase !== 'sent') return;
    const id = window.setTimeout(() => close.current(), SENT_FOR_MS);
    return () => window.clearTimeout(id);
  }, [phase]);

  const send = async () => {
    if (!reason) return;
    setPhase('sending');
    try {
      await onSubmit(reason, comment.trim());
      setPhase('sent');
    } catch (err) {
      setPhase(isTooManyReports(err) ? 'tooMany' : 'failed');
    }
  };

  const card = 'mx-3 mb-2 shrink-0 animate-pc-in rounded-lg border border-line bg-white p-2.5 shadow-[0_6px_20px_rgba(0,0,0,.08)]';
  if (phase === 'sent') {
    return (
      <div className={card} role="status" data-testid="report-panel">
        <p className="text-[12px] text-muted">{t('report.sent')}</p>
      </div>
    );
  }

  const sending = phase === 'sending';
  return (
    <section aria-label={t('report.title')} className={`${card} flex flex-col gap-2`} data-testid="report-panel">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[12px] font-medium leading-snug text-ink">{t('report.title')}</p>
          <p className="mt-0.5 text-[11.5px] leading-snug text-muted">{t('report.intro')}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          title={t('report.close')}
          aria-label={t('report.close')}
          className="text-[13px] leading-none text-muted transition-colors hover:text-ink"
        >
          ×
        </button>
      </div>

      <div role="radiogroup" aria-label={t('report.reason')} className="flex flex-wrap gap-1.5">
        {REPORT_REASONS.map((r) => (
          <button
            key={r}
            type="button"
            role="radio"
            aria-checked={reason === r}
            disabled={sending}
            onClick={() => setReason(r)}
            className={`rounded-full border px-2 py-[3px] text-[11px] transition-colors ${reason === r ? CHOSEN : IDLE}`}
          >
            {t(`report.reasons.${r}`)}
          </button>
        ))}
      </div>

      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={3}
        maxLength={2000}
        disabled={sending}
        placeholder={t('report.commentPlaceholder')}
        className="w-full resize-none rounded-md border border-line bg-white p-2 text-[12px] leading-snug text-ink placeholder:text-fainter focus:border-accent focus:outline-hidden"
      />

      {(phase === 'failed' || phase === 'tooMany') && (
        <p role="alert" className="text-[11.5px] leading-snug text-danger">{t(phase === 'failed' ? 'report.failed' : 'report.tooMany')}</p>
      )}

      <div className="flex items-center justify-end gap-3 text-[11.5px]">
        <button type="button" onClick={onClose} disabled={sending} className="text-muted transition-colors hover:text-ink disabled:text-fainter">
          {t('report.cancel')}
        </button>
        <button
          type="button"
          onClick={() => void send()}
          disabled={!reason || sending || phase === 'tooMany'}
          className="font-medium text-accent transition-colors hover:text-accent-dark disabled:text-fainter"
        >
          {sending ? t('report.sending') : t('report.send')}
        </button>
      </div>
    </section>
  );
}
